import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import LanaguagesLogo from "../languagesLogo/lanaguagesLogo";
import { ParamProjectItem, ProjectProp } from "../../components/projects/projectItem/model";
import { useHistory } from "react-router-dom";
// import "./ChromaGrid.css";

export interface ChromaItem {
  image: string;
  title: string;
  subtitle: string;
  handle?: string;
  location?: string;
  borderColor?: string;
  gradient?: string;
  url?: string;
}

export interface ChromaGridProps {
  items?: ParamProjectItem[];
  className?: string;
  radius?: number;
  columns?: number;
  rows?: number;
  damping?: number;
  fadeOut?: number;
  ease?: string;
}

type SetterFn = (v: number | string) => void;

export const ChromaGrid: React.FC<ChromaGridProps> = ({ items, className = "", radius = 300, columns = 3, rows = 2, damping = 0.45, fadeOut = 0.6, ease = "power3.out" }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const fadeRef = useRef<HTMLDivElement>(null);
  const setX = useRef<SetterFn | null>(null);
  const setY = useRef<SetterFn | null>(null);
  const pos = useRef({ x: 0, y: 0 });
  const theme = useTheme();
  
  const ltsm = useMediaQuery(theme.breakpoints.down("sm"));
  const ltmd = useMediaQuery(theme.breakpoints.down("md"));
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const history = useHistory();

  const data = items?.length ? items : [];

  const navigateTo = (value: any) => {
    let path = `/explore/project/${value.id}`;
    history.push({ pathname: path, state: value });
  };

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    setX.current = gsap.quickSetter(el, "--x", "px") as SetterFn;
    setY.current = gsap.quickSetter(el, "--y", "px") as SetterFn;
    const { width, height } = el.getBoundingClientRect();
    pos.current = { x: width / 2, y: height / 2 };
    setX.current(pos.current.x);
    setY.current(pos.current.y);
  }, []);

  const moveTo = (x: number, y: number) => {
    gsap.to(pos.current, {
      x,
      y,
      duration: damping,
      ease,
      onUpdate: () => {
        setX.current?.(pos.current.x);
        setY.current?.(pos.current.y);
      },
      overwrite: true,
    });
  };

  const handleMove = (e: React.PointerEvent) => {
    const r = rootRef.current!.getBoundingClientRect();
    moveTo(e.clientX - r.left, e.clientY - r.top);
    gsap.to(fadeRef.current, { opacity: 0, duration: 0.25, overwrite: true });
  };

  const handleLeave = () => {
    gsap.to(fadeRef.current, {
      opacity: 1,
      duration: fadeOut,
      overwrite: true,
    });
  };

  const handleCardClick = (url?: string) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleCardMove: React.MouseEventHandler<HTMLElement> = (e) => {
    const card = e.currentTarget as HTMLElement;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    card.style.setProperty("--mouse-x", `${x}px`);
    card.style.setProperty("--mouse-y", `${y}px`);
  };

  return (
    <div
      ref={rootRef}
      className={`chroma-grid ${className}`}
      style={
        {
          "--r": `${radius}px`,
          "--cols": ltsm ? 1 : ltmd ? 2 : columns,
          "--rows": rows,
        } as React.CSSProperties
      }
      onPointerMove={handleMove}
      onPointerLeave={handleLeave}
    >
      {data.map((c, i) => (
        <article
          key={i}
          className="chroma-card"
          onMouseMove={handleCardMove}
          // onClick={() => handleCardClick(c.url)}
          style={
            {
              "--card-border": c.borderColor || "transparent",
              "--card-gradient": c.gradient,
              cursor: "pointer",
            } as React.CSSProperties
          }
        >
          {/* <div className="chroma-img-wrapper">
            <img src={c.image} alt={c.title} loading="lazy" />
          </div>
          <h3 className="name">{c.title}</h3>
          <footer className="chroma-info">
            {c.handle && <span className="handle">{c.handle}</span>}
            <p className="role">{c.subtitle}</p>
            {c.location && <span className="location">{c.location}</span>}
          </footer> */}

          <div className="ui-cursor--default">
            <Box display="flex" flexDirection="row" px={2} py={3}>
              <h5 className={"ui-typo--h5 app-summary ui-nowrap ui-text--capitalize " + (ltmd ? "" : "app-font-size--medium ui-font-weight--bold")}>{c.title}</h5>
              <Box flex={1}></Box>
              <div>
                <div className="ui-flex--justify-center ui-flex--align-vertical ui-glass-pill ui-padding--2-8 ui-border--r24">
                  <p className="ui-typo--body3  ui-text--capitalize ui-font-weight--normal">{c.type}</p>
                </div>
              </div>
            </Box>
            <div className="ui-padding--8"></div>
            <Box minHeight={ltsm ? "" : ltmd ? 112 : 124} maxHeight={124} px={2}>
              <p className="ui-typo--body2 ui-font-weight--light app-summary">{c.summary}</p>
            </Box>
            <div className="ui-padding--6"></div>
            <LanaguagesLogo languages={c.languages}></LanaguagesLogo>
            <div className="ui-padding--8"></div>
            <footer className="chroma-info">
              <Button variant={prefersDarkMode ? "contained" : "contained"} fullWidth color={prefersDarkMode ? "primary" : "primary"} onClick={() => navigateTo(c)}>
                <span className="ui-typo--h6  ui-text--capitalize ui-font-weight--bold"> Explore</span>
              </Button>
            </footer>
          </div>
        </article>
      ))}
      <div className="chroma-overlay" />
      <div ref={fadeRef} className="chroma-fade" />
    </div>
  );
};

export default ChromaGrid;
